import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@codes/ui-kit/components/ui/card';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { ProjectView } from '@lemoncloud/codes-goods-api';
import { useProductService } from '@web/app/features/home/hooks';
import { useProjectSettingDialog } from '@web/app/features/home/hooks';
import { useSelectedStore, useUpdateQueryData } from '@web/app/shared';
import { ChevronLeft } from 'lucide-react';

import { projectsKeys, useDeleteProject, useUpdateProject } from '@codes/project';
import { PaginationType } from '@codes/shared';
import {
    Button,
    Input,
    Label,
    PageDialog,
    PageDialogClose,
    PageDialogContent,
    PageDialogHeader,
    PageDialogTitle,
    Separator,
    SharedSpinner,
} from '@codes/ui-kit';

export const ProjectSettingDialog = () => {
    const { isOpen, project, close } = useProjectSettingDialog();
    const { setProject, workspace } = useSelectedStore();
    const { myProducts } = useProductService();

    const updateProjectQueryData = useUpdateQueryData<PaginationType<ProjectView[]>>();
    const { t } = useTranslation();

    const { mutateAsync: deleteProject, isPending: isDeleting } = useDeleteProject();
    const { mutateAsync: updateProject, isPending: isUpdating } = useUpdateProject();

    const [projectName, setProjectName] = useState(project?.name || '');
    const isProjectInputDisabled =
        projectName === project?.name || !projectName || isUpdating || projectName.trim().length === 0;

    const updateButtonTapped = async () => {
        if (!project || !project.id || !workspace || !workspace.id) {
            return;
        }

        const updatedProject = await updateProject({ projectId: project.id, name: projectName.trim() });

        setProject(updatedProject);
        toast({ description: t('project.setting.completeToUpdateProject') });

        const queryKey = projectsKeys.list({ workspaceId: workspace.id, params: { limit: 50, page: 0 } });

        updateProjectQueryData({
            queryKey,
            updater: oldData => {
                if (!oldData) {
                    return { total: 0, data: [], list: [], page: 0 };
                }
                const updatedData = oldData.data.map(oldProject => {
                    if (oldProject.id === updatedProject.id) {
                        return updatedProject;
                    }
                    return oldProject;
                });
                return {
                    ...oldData,
                    data: updatedData,
                    list: updatedData,
                };
            },
        });
    };

    const deleteButtonTapped = async () => {
        if (!project || !workspace || !project.id || !workspace.id) {
            return;
        }
        const isOkay = confirm(t('project.setting.confirmToDeleteProject'));

        if (!isOkay) {
            return;
        }

        const deletedProject = await deleteProject(project.id);

        setProject(null);
        toast({ description: t('project.setting.completeToDeleteProject') });

        const queryKey = projectsKeys.list({ workspaceId: workspace.id, params: { limit: 50, page: 0 } });

        updateProjectQueryData({
            queryKey,
            updater: oldData => {
                if (!oldData) {
                    return { total: 0, data: [], list: [], page: 0 };
                }
                const updatedData = oldData.data.filter(oldWorkspace => {
                    return oldWorkspace.id !== deletedProject.id;
                });
                return {
                    ...oldData,
                    data: updatedData,
                    list: updatedData,
                    total: (oldData.total || 1) - 1,
                };
            },
        });

        close();
    };

    const productsNameLabel = useMemo(() => {
        if (!myProducts || myProducts.length === 0) {
            return null;
        }

        const product = myProducts[0];
        const productsCount = myProducts.length;

        if (productsCount === 1) {
            return <div className="text-base text-sub-title">{product?.name}</div>;
        }

        return (
            <div className="text-base text-sub-title">
                {t('project.serviceCountLabel', { serviceName: product.name, count: (productsCount || 1) - 1 })}
            </div>
        );
    }, [myProducts]);

    useEffect(() => {
        if (!project) {
            return;
        }
        setProjectName(project.name ?? '');
    }, [project]);

    return (
        <PageDialog open={isOpen} onOpenChange={open => !open && close()}>
            <PageDialogContent showCloseButton={false}>
                <div className="w-full max-w-[500px] sm:pt-[56px] sm:pb-[34px]">
                    <PageDialogHeader>
                        <PageDialogTitle>{t('project.setting.title')}</PageDialogTitle>
                        <PageDialogClose asChild>
                            <button className="absolute left-6 top-[19px] z-[51]" type="button">
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                        </PageDialogClose>
                    </PageDialogHeader>
                    <div className="overflow-auto px-6 space-y-7 mt-5">
                        <Card className="py-6 px-[22px] rounded-[22px]">
                            <Label variant="form" className="text-base">
                                {t('project.setting.labelText')}
                            </Label>
                            <div className="flex flex-col gap-y-[22px]">
                                <Input type="text" value={projectName} onChange={e => setProjectName(e.target.value)} />
                                <Button size="lg" disabled={isProjectInputDisabled} onClick={updateButtonTapped}>
                                    {isUpdating ? <SharedSpinner /> : t('project.edit')}
                                </Button>
                            </div>
                        </Card>

                        <Card className="flex flex-col gap-y-[22px] py-6 px-[22px] rounded-[22px]">
                            <div className="flex flex-col">
                                <Label className="text-gray-900 font-semibold text-base">
                                    {t('workspace.setting.projectDeletionLabelText')}
                                </Label>
                                <p className="text-sm text-error mt-[10px] font-medium">
                                    {t('workspace.setting.projectDeletionContentText')}
                                </p>
                            </div>
                            <Separator className="bg-gray-200" />
                            <Card className="flex flex-col bg-gray-50 gap-y-1 rounded-[18px]">
                                <div className="flex items-center gap-[10px]">
                                    <div className="text-[18px] font-medium truncate">{project?.name || ''}</div>
                                </div>
                                {productsNameLabel}
                                <div className="flex justify-end">
                                    <button
                                        className="text-error font-medium"
                                        onClick={deleteButtonTapped}
                                        disabled={isDeleting}
                                    >
                                        {isDeleting ? <SharedSpinner /> : t('project.delete')}
                                    </button>
                                </div>
                            </Card>
                        </Card>
                    </div>
                </div>
            </PageDialogContent>
        </PageDialog>
    );
};
